import { LDClient, useLDClient } from 'launchdarkly-react-client-sdk';

import { isDev } from '~/dev/utils/isDev';

import { devFlagOverrides } from './devFlagOverrides';
import {
  DEV_DEFAULTS,
  FlagType,
  FlagValueMap,
  OBJECT_VALUE_FLAGS,
  STRING_VALUE_FLAGS,
} from './flags';
import { useFlags } from './useFlags';

// Module-level bridge used by getFlag() in non-React code (Redux thunks,
// permissions). Set via useExposeLDClient() which is called in FlagsProvider.
let ldClient: LDClient | undefined;

export const useExposeLDClient = () => {
  ldClient = useLDClient();
};

export const getFlag = <F extends FlagType>(
  featureFlag: F,
): FlagValueMap[F] => {
  if (isDev) {
    const overrides = devFlagOverrides.getSnapshot();
    if (featureFlag in overrides)
      return overrides[featureFlag] as FlagValueMap[F];
    return DEV_DEFAULTS[featureFlag];
  }

  if (
    OBJECT_VALUE_FLAGS.has(featureFlag) ||
    STRING_VALUE_FLAGS.has(featureFlag)
  ) {
    return ldClient?.variation(
      featureFlag,
      DEV_DEFAULTS[featureFlag],
    ) as FlagValueMap[F];
  }

  return Boolean(ldClient?.variation(featureFlag, false)) as FlagValueMap[F];
};

export const useFlag = <F extends FlagType>(featureFlag: F): FlagValueMap[F] =>
  useFlags()[featureFlag] as FlagValueMap[F];
